<template>
  <style-wrap>
    <pep-install/>
    <LoadingAnimation v-if="(!initialized || !minimumTimeElapsed) && !error && !this.$store.getters.images.splash"/>
    <page-wrap v-if="initialized && minimumTimeElapsed"/>
    <splash-screen v-if="(!initialized || !minimumTimeElapsed) && !error" />
    <error-screen v-if="error" :error="error"></error-screen>

    <!-- Critical path: home only — blocks shell until ready -->
    <div v-if="!initialized && !error">
      <page-renderer
        v-for="(item, name) in criticalNavigation"
        :key="'critical-' + name"
        :page="item.page"
        :render-mode="item.renderMode"
        @rendered="onCriticalRendered"
      />
    </div>

    <!-- Background warm: remaining embeddable pages after home is ready -->
    <div
      v-if="!error && activeBackgroundKeys.length"
      class="pep-background-warm"
      aria-hidden="true"
    >
      <page-renderer
        v-for="(item, name) in activeBackgroundNavigation"
        :key="'warm-' + name"
        :page="item.page"
        :render-mode="item.renderMode"
        @rendered="onBackgroundRendered"
      />
    </div>
  </style-wrap>
</template>

<script>
import {defineAsyncComponent} from "vue";
import SplashScreen from "@/components/SplashScreen";
import StyleWrap from "@/components/StyleWrap";
import ErrorScreen from "@/components/ErrorScreen";
import PepInstall from "@/components/Install";
import LoadingAnimation from "@/components/LoadingAnimation";
import {
  getBackgroundWarmPages,
  getCriticalStartupPages,
  nextBackgroundWarmKeys,
} from "@/utils/navigation";


export default {
  name: 'App',
  components: {
    LoadingAnimation,
    PepInstall,
    // eslint-disable-next-line vue/no-unused-components
    PageRenderer: defineAsyncComponent(() =>
        import('./components/PageRenderer.vue')
    ),
    ErrorScreen,
    StyleWrap,
    PageWrap: defineAsyncComponent(() =>
        import('./PageWrap.vue')
    ),
    SplashScreen
  },

  data() {
    return {
      initialized: false,
      minimumTimeElapsed: false,
      error: null,
      criticalNavigation: {},
      backgroundPages: {},
      pendingBackgroundKeys: [],
      activeBackgroundKeys: [],
      backgroundWarmStarted: false,
    }
  },

  computed: {
    activeBackgroundNavigation() {
      const active = {};
      for (const key of this.activeBackgroundKeys) {
        active[key] = this.backgroundPages[key];
      }
      return active;
    }
  },

  beforeCreate() {
    window.addEventListener('sw-register', () => this.setupPEP());
  },

  methods: {
    setupPEP() {
      setTimeout(() => {
        this.minimumTimeElapsed = true;
      }, 1000)
      if(!this.initialized) {
        this.$store.dispatch('fetchPEP').then(() => {
          this.setupHead();
          const navigation = this.$store.getters.navigation;
          // Home blocks first paint; other embeddable pages warm in the background.
          // target:_blank pages are excluded from both paths.
          this.criticalNavigation = getCriticalStartupPages(navigation);
          this.backgroundPages = getBackgroundWarmPages(navigation);
          this.pendingBackgroundKeys = Object.keys(this.backgroundPages);

          if (Object.keys(this.criticalNavigation).length === 0) {
            this.initialized = true;
            this.startBackgroundWarm();
          }
        }).catch((error) => {
          this.error = error;
        });
      }
    },

    setupHead() {
      const head = document.head;
      const faviconSVG = head.querySelector('[rel="icon"][type="image/svg+xml"]'),
            favicon16 = head.querySelector('[rel="icon"][sizes="16x16"]'),
            favicon32 = head.querySelector('[rel="icon"][sizes="32x32"]'),
            maskIcon = head.querySelector('[rel="mask-icon"]'),
            appleTouchIcon = head.querySelector('[rel="apple-touch-icon"]'),
            msTileImage = head.querySelector('meta[name="msapplication-TileImage"]'),
            msTileColor = head.querySelector('meta[name="msapplication-TileColor"]'),
            themeColor = head.querySelector('meta[name="theme-color"]'),
            appleWebAppBarStyle = head.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]'),
            appleWebAppTitle = head.querySelector('meta[name="apple-mobile-web-app-title"]'),
            title = head.querySelector('title');

      faviconSVG.setAttribute('href', this.$store.getters.icons.faviconSVG);
      favicon16.setAttribute('href', this.$store.getters.icons.favicon16);
      favicon32.setAttribute('href', this.$store.getters.icons.favicon32);
      maskIcon.setAttribute('href', this.$store.getters.icons.maskIcon);
      appleTouchIcon.setAttribute('href', this.$store.getters.icons.appleTouchIcon);
      msTileImage.setAttribute('content', this.$store.getters.icons.msTileImage);
      msTileColor.setAttribute('content', this.$store.getters.colors.themeColor);
      themeColor.setAttribute('content', this.$store.getters.colors.themeColor);
      appleWebAppBarStyle.setAttribute('content', this.$store.getters.colors.themeColor);
      appleWebAppTitle.setAttribute('content', this.$store.getters.title);
      title.innerHTML = this.$store.getters.title;
    },

    onCriticalRendered(page) {
      for (const [name, item] of Object.entries(this.criticalNavigation)) {
        if (item.page === page) {
          this.criticalNavigation[name].rendered = true;
        }
      }

      const ready = Object.values(this.criticalNavigation).every(
        (item) => item.rendered === true
      );
      if (ready) {
        this.initialized = true;
        this.startBackgroundWarm();
      }
    },

    startBackgroundWarm() {
      if (this.backgroundWarmStarted) {
        return;
      }
      this.backgroundWarmStarted = true;
      console.log(
        'PEP-Render: starting background warm for',
        this.pendingBackgroundKeys.length,
        'page(s)'
      );
      this.fillBackgroundWarmSlots();
    },

    fillBackgroundWarmSlots() {
      const nextKeys = nextBackgroundWarmKeys(
        this.pendingBackgroundKeys,
        this.activeBackgroundKeys.length
      );
      if (!nextKeys.length) {
        return;
      }
      this.pendingBackgroundKeys = this.pendingBackgroundKeys.slice(nextKeys.length);
      this.activeBackgroundKeys = this.activeBackgroundKeys.concat(nextKeys);
    },

    onBackgroundRendered(page) {
      const finishedKey = this.activeBackgroundKeys.find(
        (key) => this.backgroundPages[key] && this.backgroundPages[key].page === page
      );
      if (finishedKey) {
        this.activeBackgroundKeys = this.activeBackgroundKeys.filter(
          (key) => key !== finishedKey
        );
      }
      this.fillBackgroundWarmSlots();
    }
  }
}
</script>

<style>
.pep-background-warm {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;
  opacity: 0;
}
</style>
